import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FileText, ArrowUpRight, Sparkles, CheckCircle2, Download, AlertTriangle } from "lucide-react";
import InvoiceEditorDialog from "../components/InvoiceEditorDialog";
import { InvoiceStatusBadge } from "../components/InvoiceStatusBadge";
import useLiveSync from "../lib/useLiveSync";

export default function Invoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locFilter, setLocFilter] = useState("");
  const [statusTab, setStatusTab] = useState("ready"); // ready | outstanding | paid | all
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);   // order pending an invoice (create flow)
  const [editingDraft, setEditingDraft] = useState(null); // existing draft invoice (edit flow)
  const [creatingManual, setCreatingManual] = useState(false);
  const canCreate = user?.role === "admin";

  useEffect(() => {
    if (!canCreate) return;
    api.get("/locations")
      .then((r) => setLocations(r.data.filter((l) => l.name !== "Kitchen")))
      .catch(() => {});
  }, [canCreate]);

  const refresh = async () => {
    try {
      // Auto-drafts fire on `ready_for_pickup` and beyond, so Ready-to-Invoice
      // panel needs to include any status that generates or holds a draft.
      const [iv, o] = await Promise.all([
        api.get("/invoices"),
        canCreate ? api.get("/orders?status=pulled,ready_for_pickup,picked_up,ready_to_invoice") : Promise.resolve({ data: [] }),
      ]);
      setInvoices(iv.data);
      setOrders(o.data);
      setErr("");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoaded(true);
    }
  };
  // Live sync — franchisee sees new invoices the moment admin issues them
  useLiveSync(refresh);

  const markPaid = async (inv, nextStatus) => {
    try {
      await api.patch(`/invoices/${inv.id}/status`, { status: nextStatus });
      toast.success(
        nextStatus === "paid"
          ? `${inv.invoice_no} marked Paid`
          : `${inv.invoice_no} reverted to Pending`,
      );
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const sendInvoice = async (inv) => {
    try {
      await api.post(`/invoices/${inv.id}/send`);
      toast.success(`${inv.invoice_no} sent to franchisee`, {
        description: "They can now see it in their portal.",
      });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const invoicedOrderIds = new Set(invoices.map((i) => i.order_id));
  const pending = orders.filter((o) => !invoicedOrderIds.has(o.id));

  // Admin may filter by location AND by status tab. Franchisees never see
  // the "ready" (draft) bucket — those are hidden server-side anyway.
  const effectiveStatusTab = canCreate ? statusTab : (statusTab === "ready" ? "outstanding" : statusTab);

  const locScoped = useMemo(() =>
    locFilter ? invoices.filter((i) => i.location_id === locFilter) : invoices,
  [invoices, locFilter]);

  const filteredInvoices = useMemo(() => {
    switch (effectiveStatusTab) {
      case "ready":       return locScoped.filter((i) => i.status === "draft");
      case "outstanding": return locScoped.filter((i) => i.status === "sent" || i.status === "pending");
      case "paid":        return locScoped.filter((i) => i.status === "paid");
      default:            return locScoped;
    }
  }, [locScoped, effectiveStatusTab]);

  const counts = useMemo(() => ({
    ready:       locScoped.filter((i) => i.status === "draft").length,
    outstanding: locScoped.filter((i) => i.status === "sent" || i.status === "pending").length,
    paid:        locScoped.filter((i) => i.status === "paid").length,
    all:         locScoped.length,
  }), [locScoped]);

  const unpaidCount = locScoped.filter((i) => i.status !== "paid" && i.status !== "draft").length;
  const grandTotal = locScoped.reduce((s, i) => s + (i.total || 0), 0);
  const outstandingTotal = locScoped
    .filter((i) => i.status === "sent" || i.status === "pending")
    .reduce((s, i) => s + (i.total || 0), 0);

  const downloadCSV = () => {
    if (!filteredInvoices.length) { toast.error("No invoices to export."); return; }
    const rows = filteredInvoices.map((iv) => {
      const productSubtotal = (iv.items || [])
        .filter((it) => it.category !== "packaging")
        .reduce((s, it) => s + (it.line_total || 0), 0);
      const pkgSubtotal = (iv.items || [])
        .filter((it) => it.category === "packaging")
        .reduce((s, it) => s + (it.line_total || 0), 0);
      const paidAt = iv.status === "paid" ? (iv.paid_at || "") : "";
      return {
        invoice: iv.invoice_no, order: iv.order_no,
        location: iv.location_name, customer: iv.customer || "",
        issued: (iv.created_at || "").slice(0, 10),
        paid: (paidAt || "").slice(0, 10),
        products_subtotal: productSubtotal.toFixed(2),
        packaging_subtotal: pkgSubtotal.toFixed(2),
        total: (iv.total || 0).toFixed(2),
        status: iv.status === "sent" ? "Outstanding" : (iv.status[0].toUpperCase() + iv.status.slice(1)),
      };
    });
    const headers = ["Invoice #","Order #","Location","Customer","Date issued","Date paid",
                     "Products subtotal","Packaging subtotal","Total","Status"];
    const csv = [
      headers.join(","),
      ...rows.map((r) => [r.invoice, r.order, r.location, r.customer, r.issued, r.paid,
                          r.products_subtotal, r.packaging_subtotal, r.total, r.status]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    const locName = locFilter ? locations.find((l) => l.id === locFilter)?.name || "location" : "all";
    link.href = URL.createObjectURL(blob);
    link.download = `invoices-${locName.replace(/\s+/g, "_")}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success(`Exported ${rows.length} invoice${rows.length !== 1 ? "s" : ""}.`);
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Billing"
        title="Invoices"
        description={
          canCreate
            ? "Review every fulfilled order, apply discounts, and issue branded invoices."
            : "Your invoices — issued, pending, and paid."
        }
      />

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="invoices-summary">
        <SummaryTile label="Issued" value={invoices.length} loaded={loaded} />
        {canCreate && (
          <SummaryTile
            label="Awaiting Invoice"
            value={pending.length}
            loaded={loaded}
            accent={pending.length > 0}
          />
        )}
        <SummaryTile label="Outstanding" value={loaded ? `$${outstandingTotal.toFixed(2)}` : null} loaded={loaded} accent={unpaidCount > 0} />
        <SummaryTile label="Total Billed" value={loaded ? `$${grandTotal.toFixed(2)}` : null} loaded={loaded} />
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 mb-5 flex-wrap" data-testid="invoices-status-tabs">
        {canCreate && (
          <TabButton
            active={statusTab === "ready"}
            onClick={() => setStatusTab("ready")}
            testid="invoices-tab-ready"
            label="Ready to Invoice"
            count={counts.ready + pending.length}
            accent={pending.length + counts.ready > 0}
          />
        )}
        <TabButton
          active={statusTab === "outstanding"}
          onClick={() => setStatusTab("outstanding")}
          testid="invoices-tab-outstanding"
          label="Outstanding"
          count={counts.outstanding}
          accent={counts.outstanding > 0}
        />
        <TabButton
          active={statusTab === "paid"}
          onClick={() => setStatusTab("paid")}
          testid="invoices-tab-paid"
          label="Paid"
          count={counts.paid}
        />
        <TabButton
          active={statusTab === "all"}
          onClick={() => setStatusTab("all")}
          testid="invoices-tab-all"
          label="All"
          count={counts.all}
        />
      </div>

      {canCreate && effectiveStatusTab === "ready" && pending.length > 0 && (
        <div className="luxury-card surface-warm p-6 mb-8 fade-up" data-testid="ready-to-invoice-panel">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-9 w-9 rounded-full bg-[hsl(var(--accent-gold-soft))] flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" />
            </div>
            <div>
              <h2 className="font-display text-lg font-medium">Ready to Invoice</h2>
              <p className="text-xs text-muted-foreground">
                {pending.length} fulfilled order{pending.length !== 1 ? "s" : ""} awaiting an invoice.
              </p>
            </div>
          </div>
          {err && <div className="text-xs text-destructive mb-3">{err}</div>}
          <div className="space-y-2">
            {pending.map((o) => (
              <div key={o.id} className="flex items-center justify-between bg-card border border-border rounded-xl px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {o.order_no}{o.customer ? ` · ${o.customer}` : ""}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {o.items.length} items
                    {o.pickup_date && ` · pickup ${o.pickup_date}`}
                  </div>
                </div>
                <button
                  onClick={() => setEditing(o)}
                  data-testid={`review-invoice-${o.id}`}
                  className="h-10 px-4 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 active:scale-[0.99] transition-all"
                >
                  Review & Issue
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin: location filter + CSV export */}
      {canCreate && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label className="text-xs text-muted-foreground uppercase tracking-wider">Filter by location</label>
          <select
            value={locFilter}
            onChange={(e) => setLocFilter(e.target.value)}
            data-testid="invoices-location-filter"
            className="h-10 rounded-lg border border-input bg-card px-3 text-sm"
          >
            <option value="">All franchise locations</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          <div className="flex-1" />
          <button
            onClick={() => setCreatingManual(true)}
            data-testid="new-manual-invoice-btn"
            className="h-10 px-4 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 hover:bg-foreground/90"
          >
            <FileText className="h-4 w-4" />
            New Custom Invoice
          </button>
          <button
            onClick={downloadCSV}
            disabled={!loaded || filteredInvoices.length === 0}
            data-testid="invoices-download-csv"
            className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium inline-flex items-center gap-2 hover:bg-secondary disabled:opacity-40"
          >
            <Download className="h-4 w-4" />
            Download CSV
          </button>
        </div>
      )}

      <div className="luxury-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[920px] text-sm" data-testid="invoices-table">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="px-4 py-3 label-eyebrow font-medium">Invoice</th>
              <th className="px-3 py-3 label-eyebrow font-medium">Status</th>
              <th className="px-3 py-3 label-eyebrow font-medium">Customer</th>
              <th className="px-3 py-3 label-eyebrow font-medium">Order</th>
              <th className="px-3 py-3 label-eyebrow font-medium">Issued</th>
              <th className="px-3 py-3 label-eyebrow font-medium text-right">Total</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((iv) => (
              <InvoiceRow
                key={iv.id}
                iv={iv}
                canCreate={canCreate}
                onEdit={() => setEditingDraft(iv)}
                onSend={() => sendInvoice(iv)}
                onMarkPaid={(ns) => markPaid(iv, ns)}
                onPatched={refresh}
              />
            ))}
            {filteredInvoices.length === 0 && (
              <tr><td colSpan="7" className="px-5 py-16 text-center">
                {loaded ? (
                  <>
                    <FileText className="h-7 w-7 mx-auto mb-3 text-muted-foreground/40" />
                    <p className="font-medium text-sm">
                      {effectiveStatusTab === "ready"       ? "No drafts right now"
                       : effectiveStatusTab === "outstanding" ? "No outstanding invoices"
                       : effectiveStatusTab === "paid"      ? "No paid invoices yet"
                       : "No invoices yet"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                      {canCreate
                        ? effectiveStatusTab === "ready"
                          ? "Draft invoices appear here the moment an order is picked up."
                          : "Move an order to Picked Up, then return here to issue its invoice."
                        : "Invoices will appear here the moment admin issues one for an order you placed."}
                    </p>
                  </>
                ) : (
                  <div className="space-y-2">{[1, 2, 3].map(k => <div key={k} className="h-8 rounded shimmer" />)}</div>
                )}
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      <InvoiceEditorDialog
        order={editing}
        invoice={editingDraft}
        onClose={() => { setEditing(null); setEditingDraft(null); }}
        onIssued={refresh}
      />
      {creatingManual && (
        <InvoiceEditorDialog manual onClose={() => setCreatingManual(false)} onIssued={() => { refresh(); setStatusTab("ready"); }} />
      )}
    </Layout>
  );
}

function SummaryTile({ label, value, loaded, accent }) {
  return (
    <div className={`luxury-card p-4 ${accent ? "border-primary/30" : ""}`}>
      <div className="label-eyebrow">{label}</div>
      <div className={`font-display text-3xl font-light mt-1.5 tabular-nums ${accent ? "text-primary" : ""}`}>
        {loaded ? (value ?? "—") : <span className="inline-block w-12 h-7 rounded shimmer" />}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, label, count, testid, accent }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`h-11 px-5 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-colors ${
        active
          ? "bg-foreground text-background"
          : `bg-card border border-border ${accent ? "text-primary" : "text-muted-foreground"} hover:text-foreground`
      }`}
    >
      {label}
      {typeof count === "number" && (
        <span className={`text-[11px] tabular-nums ${active ? "opacity-70" : "opacity-60"}`}>· {count}</span>
      )}
    </button>
  );
}

/**
 * One invoice row — summary line + (for admin on a draft) an expandable
 * inline packaging-price editor. Admin types per-unit price for each
 * packaging line, hits Save, draft total recomputes, then Send ships it.
 */
function InvoiceRow({ iv, canCreate, onEdit, onSend, onMarkPaid, onPatched }) {
  const isDraft = iv.status === "draft";
  const packagingLines = (iv.items || []).filter((it) => it.category === "packaging");
  const [open, setOpen] = useState(false);
  const [prices, setPrices] = useState(() =>
    Object.fromEntries(packagingLines.map((p) => [p.item_id, p.unit_price || 0])),
  );
  const [busy, setBusy] = useState(false);

  const savePackaging = async () => {
    setBusy(true);
    try {
      const unit_prices = {};
      for (const l of packagingLines) {
        const v = Number(prices[l.item_id]);
        if (!isNaN(v)) unit_prices[l.item_id] = v;
      }
      await api.patch(`/invoices/${iv.id}`, { unit_prices });
      toast.success("Packaging prices saved · total recomputed");
      setOpen(false);
      onPatched?.();
    } catch (e) {
      toast.error("Couldn't save prices", { description: formatApiError(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  return (
    <>
      <tr className="border-t border-border hover:bg-secondary/30 transition-colors">
        <td className="px-4 py-3.5">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium whitespace-nowrap">{iv.invoice_no}</span>
          </div>
        </td>
        <td className="px-3 py-3.5"><InvoiceStatusBadge status={iv.status} /></td>
        <td className="px-3 py-3.5 text-muted-foreground truncate max-w-[12rem]">{iv.customer || iv.location_name}</td>
        <td className="px-3 py-3.5 text-muted-foreground whitespace-nowrap">{iv.order_no || "Custom"}</td>
        <td className="px-3 py-3.5 text-muted-foreground whitespace-nowrap">{new Date(iv.created_at).toLocaleDateString()}</td>
        <td className="px-3 py-3.5 text-right font-medium tabular-nums whitespace-nowrap">${iv.total.toFixed(2)}</td>
        <td className="px-3 py-3.5 text-right space-x-1.5 whitespace-nowrap">
          {canCreate && isDraft && packagingLines.length > 0 && (
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid={`inline-edit-packaging-${iv.id}`}
              title={open ? "Hide packaging editor" : "Edit packaging unit prices"}
              className="text-[11px] font-medium inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
            >
              {open ? "Hide" : "Edit $"}
            </button>
          )}
          {canCreate && isDraft && (
            <>
              <button
                onClick={onEdit}
                data-testid={`edit-draft-invoice-${iv.id}`}
                title="Open the full invoice editor"
                className="text-[11px] font-medium inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
              >Editor</button>
              {iv.shortage_review?.status === "pending" ? (
                <Link to={`/invoices/${iv.id}`}
                  data-testid={`review-shortage-row-${iv.id}`}
                  title="Kitchen stock was short at pickup — choose billable quantities first"
                  className="text-[11px] font-medium inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-[#B7791F]/40 text-[#B7791F] bg-[#FFF8EB] hover:bg-[#FDEFD3]"
                ><AlertTriangle className="h-3 w-3" /> Review shortage</Link>
              ) : (
                <button
                  onClick={onSend}
                  data-testid={`send-invoice-row-${iv.id}`}
                  title="Send this invoice to the franchisee"
                  className="text-[11px] font-medium inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-foreground text-background hover:bg-foreground/90"
                ><CheckCircle2 className="h-3 w-3" /> Send</button>
              )}
            </>
          )}
          {canCreate && iv.status !== "paid" && !isDraft && (
            <>
              <button
                onClick={onEdit}
                data-testid={`edit-invoice-${iv.id}`}
                title="Edit this invoice (franchisee is notified of the update)"
                className="text-[11px] font-medium inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
              >Edit</button>
              <button
                onClick={() => onMarkPaid("paid")}
                data-testid={`mark-paid-${iv.id}`}
                className="text-[11px] font-medium inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-[#2E7D32]/30 text-[#2E7D32] hover:bg-[#E8F2EA]"
              ><CheckCircle2 className="h-3 w-3" /> Paid</button>
            </>
          )}
          {canCreate && iv.status === "paid" && (
            <button
              onClick={() => onMarkPaid("pending")}
              data-testid={`mark-pending-${iv.id}`}
              className="text-[11px] font-medium inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-border text-muted-foreground hover:bg-secondary"
            >Revert</button>
          )}
          <Link
            to={`/invoices/${iv.id}`}
            data-testid={`view-invoice-${iv.id}`}
            className="text-[11px] font-medium inline-flex items-center gap-1 hover:text-primary"
          >View <ArrowUpRight className="h-3 w-3" /></Link>
        </td>
      </tr>
      {canCreate && isDraft && open && packagingLines.length > 0 && (
        <tr className="bg-secondary/40 border-t border-border" data-testid={`packaging-editor-${iv.id}`}>
          <td colSpan="7" className="px-5 py-4">
            <div className="flex items-start gap-6 flex-wrap">
              <div className="min-w-0">
                <div className="label-eyebrow mb-2">Packaging — set unit price</div>
                <div className="space-y-2">
                  {packagingLines.map((p) => (
                    <div key={p.item_id} className="flex items-center gap-3 text-sm">
                      <div className="min-w-[14rem]">{p.name}</div>
                      <div className="text-xs text-muted-foreground w-16 text-right">× {p.quantity}</div>
                      <span className="text-xs text-muted-foreground">$</span>
                      <input
                        type="number" min="0" step="0.01" inputMode="decimal"
                        value={prices[p.item_id] ?? 0}
                        onChange={(e) => setPrices((pr) => ({ ...pr, [p.item_id]: e.target.value }))}
                        data-testid={`packaging-price-${iv.id}-${p.item_id}`}
                        className="h-10 w-28 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <div className="text-xs text-muted-foreground tabular-nums">
                        = ${((Number(prices[p.item_id]) || 0) * p.quantity).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-1" />
              <button
                onClick={savePackaging}
                disabled={busy}
                data-testid={`save-packaging-${iv.id}`}
                className="h-11 px-5 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-40"
              >
                {busy ? "Saving…" : "Save packaging prices"}
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
